import React, {useEffect, useState} from "react";
import FlowCard from "../components/FlowCard";
import DownloadABHACard from "../components/DownloadABHACard";
import WorkflowStorage from "../ABDMWorkflowStorage";
import WorkflowStatus from "../components/WorkflowStatus";
// ============================================================
// STEPS
// ============================================================
const STEPS = {
    // Existing ABHA
    SEARCH: "SEARCH",
    LOGIN_REQUEST_OTP: "LOGIN_REQUEST_OTP",
    LOGIN_VERIFY_OTP: "LOGIN_VERIFY_OTP",
    PROFILE: "PROFILE", // New ABHA
    USER_NOT_FOUND: "USER_NOT_FOUND",
    AADHAAR_REQUEST_OTP: "AADHAAR_REQUEST_OTP",
    AADHAAR_VERIFY_OTP: "AADHAAR_VERIFY_OTP",
    ADDRESS_SUGGESTION: "ADDRESS_SUGGESTION",
    ADDRESS_CREATE: "ADDRESS_CREATE",
    MOBILE_REQUEST_OTP: "MOBILE_REQUEST_OTP",
    MOBILE_VERIFY_OTP: "MOBILE_VERIFY_OTP", // Common
    DOWNLOAD_CARD: "DOWNLOAD_CARD",
    COMPLETE: "COMPLETE"
};
// ============================================================
// COMPONENT
// ============================================================
export default function RegistrationWizard() {
    const [step, setStep] = useState(STEPS.SEARCH);
    const [workflow, setWorkflow] = useState({});
    // ========================================================
    // REFRESH WORKFLOW
    // ========================================================
    const refreshWorkflow = () => {
        const data = WorkflowStorage.get() || {};
        console.log("🔄 WORKFLOW REFRESH:", data);
        setWorkflow(data);
    };
    // ========================================================
    // INITIALIZE
    // ========================================================
    useEffect(() => {
        refreshWorkflow();
        const refresh = () => refreshWorkflow();
        window.addEventListener("workflowChanged", refresh);
        return () => {
            window.removeEventListener("workflowChanged", refresh);
        };
    }, []);
    // ========================================================
    // RESTART
    // ========================================================
    const restart = () => {
        console.log("🔄 RESTARTING ABHA WORKFLOW");
        WorkflowStorage.clear();
        setWorkflow({});
        setStep(STEPS.SEARCH);
    };
    // ========================================================
    // PROGRESS
    // ========================================================
    const progress = [{
        key: STEPS.SEARCH, title: "Search Mobile"
    }, {
        key: STEPS.LOGIN_REQUEST_OTP, title: "Login OTP"
    }, {
        key: STEPS.LOGIN_VERIFY_OTP, title: "Verify Login OTP"
    }, {
        key: STEPS.PROFILE, title: "Profile"
    }, {
        key: STEPS.AADHAAR_REQUEST_OTP, title: "Aadhaar OTP"
    }, {
        key: STEPS.AADHAAR_VERIFY_OTP, title: "Verify Aadhaar & Create ABHA"
    }, {
        key: STEPS.ADDRESS_SUGGESTION, title: "Address Suggestion"
    }, {
        key: STEPS.ADDRESS_CREATE, title: "Create ABHA Address"
    }, {
        key: STEPS.MOBILE_REQUEST_OTP, title: "Mobile OTP"
    }, {
        key: STEPS.MOBILE_VERIFY_OTP, title: "Verify Mobile OTP"
    }, {
        key: STEPS.DOWNLOAD_CARD, title: "ABHA Card"
    }, {
        key: STEPS.COMPLETE, title: "Completed"
    }];
    // ========================================================
    // UI
    // ========================================================
    return (<div className="container-fluid">

        <div className="row">


            {/* ==================================================
                    LEFT
                ================================================== */}

            <div className="col-lg-8">


                {/* ==================================================
                        HEADER
                    ================================================== */}

                <div className="d-flex justify-content-between align-items-center mb-4">

                    <div>

                        <h3 className="mb-1">

                            ABHA Registration Wizard

                        </h3>

                        <small className="text-muted">

                            Dynamic Step-by-Step Testing

                        </small>

                    </div>


                    <button
                        className="btn btn-outline-danger"
                        onClick={restart}
                    >

                        Restart Wizard

                    </button>

                </div>


                {/* ==================================================
                        STEP 1
                        SEARCH ABHA BY MOBILE
                    ================================================== */}

                {step === STEPS.SEARCH && (<FlowCard

                    title="Search ABHA By Mobile"

                    description="Search existing ABHA records using mobile number."

                    endpoint="abdm/search-abha-mobile/"

                    buttonText="Search"


                    beforeSubmit={() => {
                        WorkflowStorage.clear();
                    }}


                    fields={[{
                        name: "mobile", label: "Mobile Number", required: true, maxLength: 10, placeholder: "9876543210"
                    }]}


                    save={{
                        txnId: "txnId",
                        index: "ABHA[0].index",
                        abhaNumber: "ABHA[0].ABHANumber",
                        name: "ABHA[0].name",
                        mobile: "mobile"
                    }}


                    afterSuccess={() => {
                        console.log("✅ ABHA FOUND");
                        setStep(STEPS.LOGIN_REQUEST_OTP);
                    }}


                    afterError={(err) => {
                        console.log("❌ SEARCH ERROR:", err);
                        if (err.status === 404 && err.data?.error?.code === "ABDM-1114") {
                            /*
                             * Important:
                             *
                             * Search mobile may not have been
                             * saved because the API returned
                             * an error.
                             *
                             * Therefore the Aadhaar step
                             * will explicitly ask for the
                             * mobile number.
                             */
                            setStep(STEPS.USER_NOT_FOUND);
                            return;
                        }
                        alert(JSON.stringify(err));
                    }}

                />)}


                {/* ==================================================
                        USER NOT FOUND
                    ================================================== */}

                {step === STEPS.USER_NOT_FOUND && (<div className="card shadow-sm">


                    <div className="card-header bg-warning">

                        <strong>

                            ABHA Not Found

                        </strong>

                    </div>


                    <div className="card-body text-center">


                        <h4 className="mb-3">

                            No ABHA Account Found

                        </h4>


                        <p className="text-muted">

                            No ABHA account is linked with
                            this mobile number.

                        </p>


                        <div className="alert alert-info">

                            You can create a new ABHA account
                            using Aadhaar verification.

                        </div>


                        <div className="d-flex justify-content-center gap-3 mt-4">


                            <button

                                className="btn btn-success"

                                onClick={() => {
                                    setStep(STEPS.AADHAAR_REQUEST_OTP);
                                }}

                            >

                                Create New ABHA

                            </button>


                            <button

                                className="btn btn-secondary"

                                onClick={() => {
                                    WorkflowStorage.clear();
                                    setWorkflow({});
                                    setStep(STEPS.SEARCH);
                                }}

                            >

                                Search Another Mobile

                            </button>


                        </div>


                    </div>

                </div>)}


                {/* ==================================================
                        EXISTING ABHA
                        REQUEST LOGIN OTP
                    ================================================== */}

                {step === STEPS.LOGIN_REQUEST_OTP && (<FlowCard

                    title="Request Login OTP"

                    endpoint="abdm/request-abha-otp/"

                    buttonText="Generate OTP"


                    prefill={{
                        txnId: "txnId", index: "index"
                    }}


                    requiredWorkflow={["txnId", "index"]}


                    save={{
                        txnId: "txnId"
                    }}


                    fields={[{
                        name: "txnId", label: "Search Transaction", readOnly: true
                    }, {
                        name: "index", label: "ABHA Index", readOnly: true
                    }]}


                    afterSuccess={() => {
                        setStep(STEPS.LOGIN_VERIFY_OTP);
                    }}

                />)}


                {/* ==================================================
                        EXISTING ABHA
                        VERIFY LOGIN OTP
                    ================================================== */}

                {step === STEPS.LOGIN_VERIFY_OTP && (<FlowCard

                    title="Verify Login OTP"

                    endpoint="abdm/verify-abha-otp/"

                    buttonText="Verify OTP"


                    prefill={{
                        txnId: "txnId"
                    }}


                    requiredWorkflow={["txnId"]}


                    save={{
                        xToken: "token", refreshToken: "refreshToken", abhaNumber: "accounts[0].ABHANumber"
                    }}


                    fields={[{
                        name: "txnId", hidden: true, readOnly: true
                    }, {
                        name: "otp", label: "OTP", required: true, maxLength: 6, placeholder: "Enter 6-digit OTP"
                    }]}


                    afterSuccess={() => {
                        setStep(STEPS.PROFILE);
                    }}

                />)}


                {/* ==================================================
                        EXISTING ABHA
                        PROFILE
                    ================================================== */}

                {step === STEPS.PROFILE && (<FlowCard

                    title="Get ABHA Profile"

                    endpoint="abdm/get-abha-profile/"

                    method="GET"

                    buttonText="Load Profile"

                    fields={[]}


                    afterSuccess={() => {
                        setStep(STEPS.DOWNLOAD_CARD);
                    }}

                />)}


                {/* ==================================================
                        NEW ABHA
                        REQUEST AADHAAR OTP
                    ================================================== */}

                {step === STEPS.AADHAAR_REQUEST_OTP && (<FlowCard

                    title="Request Aadhaar OTP"

                    description={"Enter Aadhaar number and the mobile number " + "to be used for ABHA communication."}

                    endpoint="abdm/request-aadhaar-otp/"

                    buttonText="Generate Aadhaar OTP"

                    fields={[{
                        name: "aadhaar",
                        label: "Aadhaar Number",
                        required: true,
                        maxLength: 12,
                        placeholder: "Enter 12-digit Aadhaar number"
                    }, {
                        name: "mobile",
                        label: "Mobile Number",
                        required: true,
                        maxLength: 10,
                        placeholder: "Enter 10-digit mobile number"
                    }]}

                    save={{
                        txnId: "txnId", mobile: "mobile"
                    }}

                    afterSuccess={() => {
                        console.log("✅ AADHAAR OTP GENERATED");
                        console.log("WORKFLOW:", WorkflowStorage.get());
                        setStep(STEPS.AADHAAR_VERIFY_OTP);
                    }}

                />)}


                {/* ==================================================
                        NEW ABHA
                        VERIFY AADHAAR OTP + CREATE ABHA
                    ================================================== */}

                {step === STEPS.AADHAAR_VERIFY_OTP && (<FlowCard

                    title="Verify Aadhaar OTP & Create ABHA"

                    description={"Enter the OTP received on your " + "Aadhaar-linked mobile number."}

                    endpoint="abdm/enrol-abha-by-aadhaar/"

                    buttonText="Verify OTP & Create ABHA"

                    prefill={{
                        txnId: "txnId", mobile: "mobile"
                    }}

                    requiredWorkflow={["txnId", "mobile"]}

                    save={{
                        txnId: "txnId",
                        xToken: "token",
                        refreshToken: "refreshToken",
                        abhaNumber: "abhaNumber",
                        abhaAddress: "abhaAddress"
                    }}

                    fields={[{
                        name: "txnId", hidden: true, readOnly: true
                    }, {
                        name: "mobile", hidden: true, readOnly: true
                    }, {
                        name: "otp",
                        label: "Aadhaar OTP",
                        required: true,
                        maxLength: 6,
                        placeholder: "Enter 6-digit OTP"
                    }]}

                    afterSuccess={() => {
                        console.log("✅ ABHA CREATED");
                        console.log("WORKFLOW:", WorkflowStorage.get());
                        setStep(STEPS.ADDRESS_SUGGESTION);
                    }}

                />)}


                {/* ==================================================
                        ADDRESS SUGGESTION
                    ================================================== */}

                {step === STEPS.ADDRESS_SUGGESTION && (<FlowCard

                    title="Get ABHA Address Suggestions"

                    description={"Fetch available ABHA address suggestions " + "for the newly created ABHA."}

                    endpoint="abdm/abha-address-suggestion/"

                    buttonText="Load Suggestions"


                    prefill={{
                        txnId: "txnId"
                    }}


                    requiredWorkflow={["txnId"]}


                    save={{
                        txnId: "txnId", abhaAddress: "abhaAddress"
                    }}


                    fields={[{
                        name: "txnId", hidden: true, readOnly: true
                    }]}


                    afterSuccess={() => {
                        console.log("✅ ADDRESS SUGGESTIONS RECEIVED");
                        setStep(STEPS.ADDRESS_CREATE);
                    }}

                />)}


                {/* ==================================================
                        CREATE ABHA ADDRESS
                    ================================================== */}

                {step === STEPS.ADDRESS_CREATE && (<FlowCard

                    title="Create ABHA Address"

                    description={"Create the selected ABHA address."}

                    endpoint="abdm/create-abha-address-aadhar/"

                    buttonText="Create Address"


                    prefill={{
                        txnId: "txnId", abhaAddress: "abhaAddress"
                    }}


                    requiredWorkflow={["txnId", "abhaAddress"]}


                    save={{
                        txnId: "txnId", abhaAddress: "abhaAddress"
                    }}


                    fields={[{
                        name: "txnId", hidden: true, readOnly: true
                    }, {
                        name: "abhaAddress", label: "ABHA Address", readOnly: true
                    }]}


                    afterSuccess={() => {
                        console.log("✅ ABHA ADDRESS CREATED");
                        setStep(STEPS.MOBILE_REQUEST_OTP);
                    }}

                />)}


                {/* ==================================================
                        REQUEST MOBILE OTP
                    ================================================== */}

                {step === STEPS.MOBILE_REQUEST_OTP && (<FlowCard

                    title="Request Mobile OTP"

                    description={"Send OTP to verify the mobile number " + "associated with the ABHA account."}

                    endpoint="abdm/abha-request-mobile-otp/"

                    buttonText="Send OTP"


                    prefill={{
                        txnId: "txnId", mobile: "mobile"
                    }}


                    requiredWorkflow={["txnId", "mobile"]}


                    save={{
                        txnId: "txnId"
                    }}


                    fields={[{
                        name: "txnId", hidden: true, readOnly: true
                    }, {
                        name: "mobile", label: "Mobile Number", readOnly: true
                    }]}


                    afterSuccess={() => {
                        console.log("✅ MOBILE OTP SENT");
                        setStep(STEPS.MOBILE_VERIFY_OTP);
                    }}

                />)}


                {/* ==================================================
                        VERIFY MOBILE OTP
                    ================================================== */}

                {step === STEPS.MOBILE_VERIFY_OTP && (<FlowCard

                    title="Verify Mobile OTP"

                    description={"Enter the OTP received on the mobile number."}

                    endpoint="abdm/abha-verify-mobile-otp/"

                    buttonText="Verify Mobile OTP"


                    prefill={{
                        txnId: "txnId"
                    }}


                    requiredWorkflow={["txnId"]}


                    save={{
                        txnId: "txnId"
                    }}


                    fields={[{
                        name: "txnId", hidden: true, readOnly: true
                    }, {
                        name: "otp", label: "Mobile OTP", required: true, maxLength: 6, placeholder: "Enter 6-digit OTP"
                    }]}


                    afterSuccess={() => {
                        console.log("✅ MOBILE OTP VERIFIED");
                        setStep(STEPS.DOWNLOAD_CARD);
                    }}

                />)}


                {/* ==================================================
                        DOWNLOAD ABHA CARD
                    ================================================== */}

                {step === STEPS.DOWNLOAD_CARD && (<DownloadABHACard

                    afterSuccess={() => {
                        setStep(STEPS.COMPLETE);
                    }}

                />)}


                {/* ==================================================
                        COMPLETE
                    ================================================== */}

                {step === STEPS.COMPLETE && (<div className="card border-success shadow-sm">

                    <div className="card-body text-center p-5">


                        <div
                            className="display-2 mb-3"
                            style={{
                                color: "#16a34a"
                            }}
                        >

                            ✓

                        </div>


                        <h2 className="text-success">

                            ABHA Workflow Completed

                        </h2>


                        <p className="text-muted mt-3">

                            All required ABHA registration
                            APIs have been executed successfully.

                        </p>


                        <button

                            className="btn btn-primary mt-3"

                            onClick={() => {
                                WorkflowStorage.clear();
                                setWorkflow({});
                                setStep(STEPS.SEARCH);
                            }}

                        >

                            Start New Registration

                        </button>


                    </div>

                </div>)}

            </div>


            {/* ==================================================
                    RIGHT SIDE
                ================================================== */}

            <div className="col-lg-4">


                {/* ==================================================
                        PROGRESS
                    ================================================== */}

                <div className="card shadow-sm mb-3">


                    <div className="card-header">

                        <strong>
                            Progress
                        </strong>

                    </div>


                    <div className="card-body">


                        <ul className="list-group">


                            {progress.map((item, index) => (<li

                                key={item.key}

                                className={`list-group-item ` + `d-flex ` + `justify-content-between ` + `align-items-center ` + `${step === item.key ? "active" : ""}`}

                            >

                                            <span>

                                                {index + 1}.{" "}

                                                {item.title}

                                            </span>


                                {step === item.key && (<span
                                    className={"badge " + "bg-light " + "text-dark"}
                                >

                                                    Current

                                                </span>)}

                            </li>))}

                        </ul>

                    </div>

                </div>


                {/* ==================================================
                        WORKFLOW STATUS
                    ================================================== */}

                <WorkflowStatus/>


                {/* ==================================================
                        DEBUG WORKFLOW
                    ================================================== */}

                <div className="card shadow-sm mt-3">

                    <div className="card-header">

                        <strong>
                            Current Workflow
                        </strong>

                    </div>


                    <div className="card-body">

                            <pre
                                style={{
                                    fontSize: "11px", maxHeight: "350px", overflow: "auto"
                                }}
                            >

                                {JSON.stringify(workflow, null, 2)}

                            </pre>

                    </div>

                </div>

            </div>

        </div>

    </div>);
}